import { useState, useCallback } from 'react'
import { buildImageUrl, generateSeed } from '@/lib/utils'
import type { GenerationResult, GenerationOptions } from '@/types'

export function useImageGenerator() {
  const [isGenerating, setIsGenerating] = useState(false)
  const [result, setResult] = useState<GenerationResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [progress, setProgress] = useState(0)

  const generate = useCallback(async (options: GenerationOptions): Promise<GenerationResult | null> => {
    setIsGenerating(true)
    setError(null)
    setProgress(0)
    setResult(null)

    const startTime = Date.now()

    try {
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + Math.random() * 15, 90))
      }, 500)

      const seed = generateSeed()
      const [w, h] = options.ratio.split(':').map(Number)
      const baseSize = options.quality === 'ultra' ? 1536 : options.quality === 'high' ? 1280 : 1024
      const width = w >= h ? baseSize : Math.round(baseSize * w / h)
      const height = h >= w ? baseSize : Math.round(baseSize * h / w)

      let enhancedPrompt = options.prompt
      if (options.style) {
        const styleMap: Record<string, string> = {
          photorealistic: 'photorealistic, highly detailed, 8k, professional photography',
          anime: 'anime style, manga art, vibrant colors, detailed line art',
          'digital-art': 'digital art, concept art, detailed illustration, vibrant colors',
          'oil-painting': 'oil painting, classical art style, rich textures, museum quality',
          watercolor: 'watercolor painting, soft edges, artistic, flowing colors',
          '3d-render': '3D render, octane render, blender, cinematic lighting, ray tracing',
          sketch: 'pencil sketch, hand drawn, graphite art, detailed shading',
          cyberpunk: 'cyberpunk style, neon lights, futuristic, dystopian, high tech',
          fantasy: 'fantasy art, magical, ethereal, otherworldly, detailed',
          minimalist: 'minimalist design, clean lines, simple, elegant, modern',
          vintage: 'vintage style, retro aesthetic, film grain, nostalgic',
        }
        enhancedPrompt += `, ${styleMap[options.style] || options.style}`
      }

      if (options.quality === 'high' || options.quality === 'ultra') {
        enhancedPrompt += ', ultra high quality, 8k, masterpiece, best quality, highly detailed'
      }

      const imageUrl = buildImageUrl(enhancedPrompt, width, height, seed)

      const img = new Image()
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve()
        img.onerror = () => reject(new Error('Failed to load generated image'))
        img.src = imageUrl
      })

      clearInterval(progressInterval)
      setProgress(100)

      const genTime = (Date.now() - startTime) / 1000

      const result: GenerationResult = {
        url: imageUrl,
        prompt: options.prompt,
        ratio: options.ratio,
        style: options.style,
        model: options.model,
        quality: options.quality,
        width,
        height,
        generationTime: genTime,
        seed,
        timestamp: Date.now(),
      }

      setResult(result)
      setIsGenerating(false)
      return result
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate image'
      setError(message)
      setIsGenerating(false)
      return null
    }
  }, [])

  const clear = useCallback(() => {
    setResult(null)
    setError(null)
    setProgress(0)
    setIsGenerating(false)
  }, [])

  return { generate, clear, isGenerating, result, error, progress }
}
